import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Lock, User, ScanBarcode, Printer, Smartphone, Monitor } from "lucide-react";
import { BRAND, homePath } from "../lib/brand";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { ThemeToggle } from "../components/ThemeToggle";
import { DesktopFirstRun } from "../components/DesktopFirstRun";
import { IS_DESKTOP } from "../lib/desktop";

const Feature = ({ icon: Icon, title, text }) => (
  <div className="flex gap-4">
    <div className="h-10 w-10 shrink-0 rounded-xl bg-white/10 grid place-items-center"><Icon className="h-5 w-5" /></div>
    <div>
      <div className="font-semibold">{title}</div>
      <div className="text-sm text-white/70">{text}</div>
    </div>
  </div>
);

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [username, setU] = useState("");
  const [password, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const next = new URLSearchParams(window.location.search).get("next") || "";
  const target = (u) => (/^\/[a-z-]+$/.test(next) ? next : homePath(u));

  if (user) {
    setTimeout(() => nav(target(user), { replace: true }));
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(username, password);
      nav(target(u), { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0B0F17] text-white relative overflow-hidden">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
        <img src={BRAND.logoFull} alt={BRAND.name} className="h-24 w-auto self-start relative" data-testid="login-brand-logo" />
        <div className="relative space-y-8 max-w-md">
          <h2 className="font-display text-4xl font-extrabold leading-tight">Kasir cepat, stok rapi, laporan jelas.</h2>
          <div className="space-y-5">
            <Feature icon={ScanBarcode} title="Scanner Barcode" text="USB, Bluetooth, serial, atau kamera HP." />
            <Feature icon={Printer} title="Printer Thermal & Laci Uang" text="Cetak struk ESC/POS 58/80mm, laci terbuka otomatis." />
            <Feature icon={Smartphone} title="Laptop & Handphone" text="Instal sebagai aplikasi di perangkat apa pun." />
          </div>
        </div>
        <div className="text-xs text-white/50 relative">© {new Date().getFullYear()} {BRAND.name} · {BRAND.tagline}</div>
      </div>
      <div className="flex flex-col p-6 sm:p-10">
        <div className="flex justify-end"><ThemeToggle /></div>
        <div className="flex-1 grid place-items-center">
          <DesktopFirstRun>
          <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
            <img src={BRAND.logoFull} alt={BRAND.name} className="lg:hidden h-20 w-auto mx-auto" />
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight">Masuk</h1>
              <p className="text-muted-foreground mt-1 text-sm">Gunakan akun owner atau kasir Anda.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Username / Email</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="username" autoFocus className="pl-9 h-11" value={username} onChange={(e) => setU(e.target.value)} data-testid="login-username-input" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Link to="/lupa-password" className="float-right text-xs text-primary hover:underline" data-testid="forgot-password-link">Lupa password?</Link>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <PasswordInput id="password" className="pl-9 h-11" value={password} onChange={(e) => setP(e.target.value)} data-testid="login-password-input" required />
              </div>
            </div>
            {err && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
            <Button type="submit" className="w-full h-11 text-base font-semibold" disabled={loading} data-testid="login-submit-btn">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Masuk
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Pemilik toko baru? <Link to="/daftar" className="text-primary font-semibold hover:underline" data-testid="go-register-link">Daftar gratis</Link>
            </p>
            {!IS_DESKTOP && (
              <Link to="/unduh" className="flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-primary" data-testid="download-desktop-link">
                <Monitor className="h-3.5 w-3.5" />Unduh aplikasi desktop Windows (bisa offline penuh)
              </Link>
            )}
          </form>
          </DesktopFirstRun>
        </div>
      </div>
    </div>
  );
}
